import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col">
      {/* Background Image */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Minimalist%20artistic%20flat%20lay%20of%20premium%20fashion%20accessories%20including%20sneakers%20bags%20sunglasses%20and%20clothing%20elegantly%20scattered%20on%20warm%20cream%20textured%20surface%20soft%20diffused%20natural%20lighting%20from%20above%20clean%20editorial%20aesthetic%20warm%20beige%20and%20taupe%20palette%20harmonious%20organic%20composition%20no%20text%20no%20logos%20modern%20luxury%20product%20photography&width=1600&height=1000&seq=hero-bg-cssbuy-v2-01&orientation=landscape"
          alt="CSSBuy Spreadsheet produtos"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center flex-1 w-full px-6 md:px-10 pt-32 pb-10">
        {/* Glassmorphism dropdown bar */}
        <div className="w-full max-w-[600px] mb-10 mt-6" ref={dropdownRef}>
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-6 py-4 bg-white/75 backdrop-blur-md rounded-xl border border-white/40 text-foreground-800 hover:bg-white/85 transition-all cursor-pointer"
            >
              <span className="text-sm md:text-base font-medium">
                Selecione uma Categoria
              </span>
              {dropdownOpen ? (
                <i className="ri-arrow-up-s-line text-foreground-600 transition-transform duration-200"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-foreground-600 transition-transform duration-200"></i>
              )}
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-md rounded-xl border border-background-200 shadow-lg overflow-hidden max-h-[420px] overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-primary-50 transition-colors cursor-pointer border-b border-background-100 last:border-0"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600">
                      <i className={`${cat.icon} text-sm`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800">{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tag */}
        <div className="inline-flex items-center px-4 py-1.5 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm mb-5">
          <span className="text-xs font-medium text-white tracking-wide">Atualizado 2026</span>
        </div>

        {/* H1 */}
        <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white text-center leading-tight max-w-[700px]">
          Melhor <strong className="font-bold">CSSBuy Spreadsheet</strong>
          <br />
          <span className="font-heading italic font-normal">em Português</span>
        </h1>

        {/* Subtitle */}
        <p className="mt-5 text-sm md:text-lg text-white/80 text-center max-w-[560px] leading-relaxed">
          Encontre produtos atualizados, vendedores recomendados e guias completos para comprar com segurança.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mt-8">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-7 py-3 text-sm font-semibold text-white border-2 border-white/60 rounded-full hover:bg-white/15 transition-all whitespace-nowrap"
          >
            Ver CSSBuy Spreadsheet
            <i className="ri-arrow-right-up-line"></i>
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-3 px-7 py-3 text-sm font-semibold bg-black/80 text-white rounded-full hover:bg-black transition-all whitespace-nowrap group"
          >
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-500 -ml-1">
              <i className="ri-shopping-bag-line text-white text-xs"></i>
            </span>
            Buy It Now
            <i className="ri-arrow-right-up-line text-white/70 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
          </a>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-12">
          {[
            { icon: 'ri-refresh-line', text: 'Atualizado diariamente' },
            { icon: 'ri-shield-check-line', text: 'Produtos selecionados' },
            { icon: 'ri-book-open-line', text: 'Guias completos' },
            { icon: 'ri-scales-line', text: 'Comparações detalhadas' },
            { icon: 'ri-lock-line', text: 'Segurança e transparência' },
          ].map((badge) => (
            <div
              key={badge.text}
              className="flex items-center gap-2 text-white/80"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${badge.icon} text-xs text-accent-400`}></i>
              </div>
              <span className="text-xs whitespace-nowrap">{badge.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}